import { pushTrack } from "utils";

import {
  type AdEngBiddersPayload,
  BidderEvent,
  type BidStatus,
} from "common-types";
import type { AdNode } from "../../../core/AdNode";

function payloadFromBid(
  bid: PrebidBidResponse,
  status: BidStatus,
  node: AdNode | undefined,
  fullAuction: boolean,
): AdEngBiddersPayload {
  const bidPayload: AdEngBiddersPayload = {
    // This configuration assumes we are bidding on repeatable slots individually instead of once as a group
    slotName: bid.adUnitCode,
    baseSlotName: node?.getBaseSlotName() ?? bid.adUnitCode,

    rv: node?.getRenderCount() ?? undefined,
    bidInfo: {
      status: status,
      bidderName: bid.bidderCode,
      size: `${bid.width}x${bid.height}`,
      price: `${bid.cpm}`,
      timeToRespond: bid.timeToRespond,

      auctionId: bid.auctionId,
      rejectionReason: bid.rejectionReason,
      fullAuction: fullAuction,
    },
  };
  return bidPayload;
}

function payloadFromBidRequest(
  bidRequest: PrebidBidRequest,
  status: BidStatus,
  node: AdNode | undefined,
  fullAuction: boolean,
): AdEngBiddersPayload {
  const bidPayload: AdEngBiddersPayload = {
    // This configuration assumes we are bidding on repeatable slots individually instead of once as a group
    slotName: bidRequest.adUnitCode,
    baseSlotName: node?.getBaseSlotName() ?? bidRequest.adUnitCode,

    rv: node?.getRenderCount() ?? undefined,
    bidInfo: {
      status: status,
      bidderName: bidRequest.bidder,
      auctionId: bidRequest.auctionId,

      // not available from bidRequest:
      size: undefined,
      price: undefined,
      timeToRespond: undefined,
      rejectionReason: undefined,
      fullAuction: fullAuction,
    },
  };
  return bidPayload;
}

export function sendBidReceived(
  bid: PrebidBidResponse,
  node: AdNode | undefined,
  fullAuction: boolean,
  actuallySendData: boolean,
): void {
  if (!actuallySendData) {
    return;
  }
  const bidPayload = payloadFromBid(bid, "on_time", node, fullAuction);
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}

export function sendRejectedBid(
  bid: PrebidBidResponse,
  node: AdNode | undefined,
  fullAuction: boolean,
  actuallySendData: boolean,
): void {
  if (!actuallySendData) {
    return;
  }

  /**
   * TODO: find out if a rejected bid includes width, height and CPM data?
   *
   */

  const bidPayload = payloadFromBid(bid, "rejected", node, fullAuction);
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}

export function sendUnknownBid(
  bidRequest: PrebidBidRequest,
  node: AdNode | undefined,
  fullAuction: boolean,
  actuallySendData: boolean,
) {
  if (!actuallySendData) {
    return;
  }
  const bidPayload = payloadFromBidRequest(
    bidRequest,
    "unknown",
    node,
    fullAuction,
  );
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}

export function sendLateBid(
  bidRequest: PrebidBidRequest,
  node: AdNode | undefined,
  fullAuction: boolean,
  actuallySendData: boolean,
) {
  if (!actuallySendData) {
    return;
  }
  const bidPayload = payloadFromBidRequest(
    bidRequest,
    "timedout",
    node,
    fullAuction,
  );
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}

export function sendDeclinedBid(
  bidRequest: PrebidBidRequest,
  node: AdNode | undefined,
  fullAuction: boolean,
  actuallySendData: boolean,
) {
  if (!actuallySendData) {
    return;
  }
  const bidPayload = payloadFromBidRequest(
    bidRequest,
    "declined",
    node,
    fullAuction,
  );
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}

/**
 * This event is sent in the catch block of each of our send events.
 * In case there is an malformed data coming in from the auction,
 * this will send a placeholder bid event, maintaining the one-to-one
 * relationship of bids requested to data records and alerting us
 * to the existence of a data problem.
 *
 * fullAuction: indicates whether the record is part of a fully-recorded auction or a partially sampled one.
 */
export function sendErrorBid(fullAuction: boolean, actuallySendData: boolean) {
  if (!actuallySendData) {
    return;
  }
  const bidPayload: AdEngBiddersPayload = {
    // TODO: these slotNames are not totally consistent with behavior, need to clean up
    slotName: "error",
    baseSlotName: "error",

    rv: undefined,
    bidInfo: {
      status: "error",
      bidderName: "error",
      auctionId: undefined,

      size: undefined,
      price: undefined,
      timeToRespond: undefined,
      rejectionReason: undefined,
      fullAuction: fullAuction,
    },
  };
  pushTrack(BidderEvent.BIDDER_RESPONSE, bidPayload);
}
